import {
  createApiQueryClient,
  type ApiQueryClient,
  type AuthedFetchOptions,
} from '@hrms/api-client';
import { createWebTokenStore } from './token-store.js';
import { requestSalaryUnlock } from '../features/salary-pin/salaryPinGate.js';

export const tokens = createWebTokenStore();

/**
 * One client for the whole app. Two separately configured clients is how an app ends up
 * refreshing the same token twice and tripping the server's reuse-detection.
 *
 * `onSessionExpired` is deliberately NOT set. The fetcher calls `tokens.clear()` before it would
 * call that hook, `clear()` notifies the store's subscribers, and `SessionProvider` observes the
 * store — so the session flips to anonymous and `RequireAuth` redirects, carrying the current
 * location as `from`. A second signal here would be a second place that navigates.
 *
 * `VITE_API_URL` is what infra/scripts/deploy.sh bakes into the production bundle; in development
 * the Vite proxy forwards `/api/*` to the API with the prefix stripped, exactly as nginx does in
 * production, so the bare `/api` default works in both.
 */
/**
 * Explicitly typed: as @hrms/shared's root contract has grown (Performance Reviews, recruitment,
 * careers, and now attendance each added more routers), tsc's project-reference build (`tsc -b`,
 * which emits declarations) can no longer serialize the inferred return type of
 * `createApiQueryClient(...)` (TS7056) — an `@hrms/api-client` growth problem, not anything
 * specific to this call site. Naming the type here (rather than a `skipLibCheck`-style workaround)
 * keeps the error where it's actually caused, and costs nothing: `ApiQueryClient` already IS this
 * exact return type. See createApiQueryClient's own header comment for the same fix applied there.
 */
/** Same base the ts-rest client above uses — exported for the one call site (the careers resume
 *  upload) that has to hit the API directly with a `fetch`, since multipart/form-data doesn't fit
 *  through the generated typed client. */
export const apiBaseUrl = import.meta.env.VITE_API_URL ?? '/api';

export const tsr: ApiQueryClient = createApiQueryClient({
  baseUrl: apiBaseUrl,
  tokens,
  refreshLock: crossTabLock,
  // 403 SALARY_PIN_REQUIRED (ADR 0041): one PIN dialog for every refused request, then each is sent again.
  onSalaryPinRequired: requestSalaryUnlock,
});

/**
 * Two tabs whose access tokens expire together would each rotate the same persisted refresh
 * token; the server sees the second as a replayed, already-consumed token and revokes the whole
 * family — both tabs logged out, from a page reload. The Web Locks API serialises the refresh
 * across every tab of this origin; the waiting tab then reads the token the winner wrote (the
 * store reads through to localStorage for exactly this reason). Falls back to unserialised where
 * the API is missing — old WebViews — rather than refusing to refresh at all.
 */
async function crossTabLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  // lib.dom types the callback's result loosely; the value resolved is exactly what `fn` resolved.
  return (await locks.request('hrms:refresh', fn)) as T;
}

/**
 * For the two calls that cannot go through ts-rest — the copilot's multipart attachment upload
 * and its SSE stream (see `@hrms/api-client`'s `authedFetch`). Same store and the SAME cross-tab
 * lock as `tsr`, so a raw call ageing out alongside a typed one still refreshes exactly once.
 */
export const authedFetchOptions: AuthedFetchOptions = {
  baseUrl: apiBaseUrl,
  tokens,
  refreshLock: crossTabLock,
  onSalaryPinRequired: requestSalaryUnlock,
};

export const ME_QUERY_KEY = ['auth', 'me'] as const;
